import { Bell, PanelLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useBreadcrumb } from '@/components/common/BreadcrumbContext';
import { TopBarUser } from '@/components/common/TopBarUser';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useAuth from '@/hooks/useAuth';
import { ROUTES } from '@/utils/constants';

interface Crumb {
  label: string;
  // null = not navigable (the current page, or a record id that has no list of
  // its own). Anything else is a real route the crumb links to.
  href: string | null;
}

// Builds one crumb per path segment, each linking to the cumulative path so
// clicking "Renewals" in /billing/renewals/12 goes back to the queue. A numeric
// segment (a record id) is replaced by the page-supplied `extra` label (e.g.
// the client name) when there is one, since /billing/renewals/12 is where we
// already are and the raw id reads as noise.
const buildCrumbs = (path: string, extra: string | null): Crumb[] => {
  const segments = path
    .replace(/^\/billing/, '')
    .split('/')
    .filter(Boolean);
  if (segments.length === 0) segments.push('dashboard');

  const crumbs: Crumb[] = [{ label: 'Billing', href: ROUTES.BILLING_DASHBOARD }];
  let acc = '/billing';
  segments.forEach((segment) => {
    acc += `/${segment}`;
    const isId = /^\d+$/.test(segment);
    const label = isId
      ? (extra ?? `#${segment}`)
      : segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    crumbs.push({ label, href: acc });
  });
  // An `extra` on a non-detail route (an in-page selection, not a URL change)
  // is appended as its own trailing crumb.
  const lastIsId = /^\d+$/.test(segments[segments.length - 1] ?? '');
  if (extra && !lastIsId) crumbs.push({ label: extra, href: null });

  // The trailing crumb is the current page - never a link.
  return crumbs.map((c, i) => (i === crumbs.length - 1 ? { ...c, href: null } : c));
};

const Breadcrumbs = ({ path, extra }: { path: string; extra: string | null }) => {
  const crumbs = buildCrumbs(path, extra);
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs">
      <span className="font-semibold text-muted-foreground">CRM</span>
      {crumbs.map((crumb, i) => (
        <span
          key={`${crumb.label}-${crumb.href ?? 'current'}`}
          className="flex items-center gap-1.5"
        >
          <span className="text-muted-foreground">/</span>
          {crumb.href ? (
            <Link
              to={crumb.href}
              className="rounded text-muted-foreground transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {crumb.label}
            </Link>
          ) : (
            <span
              className={
                i === crumbs.length - 1 ? 'font-bold text-foreground' : 'text-muted-foreground'
              }
              aria-current={i === crumbs.length - 1 ? 'page' : undefined}
            >
              {crumb.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
};

interface BillingTopBarProps {
  onToggleSidebar: () => void;
}

export const BillingTopBar = ({ onToggleSidebar }: BillingTopBarProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const { extra } = useBreadcrumb();

  return (
    <header className="sticky top-0 z-40 h-14 border-b border-border bg-card/95 backdrop-blur">
      <div className="flex h-full items-center gap-3 px-4">
        <Button variant="ghost" size="icon" onClick={onToggleSidebar} aria-label="Toggle sidebar">
          <PanelLeft className="h-5 w-5" />
        </Button>

        <div className="flex-1">
          <Breadcrumbs path={location.pathname} extra={extra} />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
              <Bell className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="border-b border-border px-3 py-2 text-sm font-bold">Notifications</div>
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              You&apos;re all caught up.
            </p>
          </PopoverContent>
        </Popover>

        <Badge
          variant="outline"
          className="hidden border-warning bg-warning/30 text-foreground sm:inline-flex"
        >
          DEV
        </Badge>

        <TopBarUser profileRoute={ROUTES.BILLING_PROFILE} roleLabel={user?.jobRole} />
      </div>
    </header>
  );
};
